import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import {
    getAudioSignedUrlService,
    uploadAudioToS3Service,
    createUploadedAudioService,
    updateUploadedAudioService
} from '../../services/content';

interface UploadAudioParams {
    org_id: string;
    integration_id: string;
    title: string;
    file: File;
    content_id?: string; // For updates
}

export const useUploadAudio = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ org_id, integration_id, title, file, content_id }: UploadAudioParams) => {
            // Step 1: Get signed URL
            const { signed_url, url } = await getAudioSignedUrlService({
                org_id,
                integration_id,
                file_name: file.name,
                content_type: file.type
            });

            // Step 2: Upload to S3
            await uploadAudioToS3Service(signed_url, file);

            // Step 3: Create or update content record
            if (content_id) {
                // Update existing audio
                return await updateUploadedAudioService({
                    org_id,
                    integration_id,
                    content_id,
                    title,
                    url
                });
            } else {
                // Create new audio
                return await createUploadedAudioService({
                    org_id,
                    integration_id,
                    title,
                    url
                });
            }
        },
        onSuccess: (_, variables) => {
            const isUpdate = Boolean(variables.content_id);
            toast.success(isUpdate ? 'Audio updated successfully' : 'Audio uploaded successfully');
            
            // Invalidate content queries to refresh the list
            queryClient.invalidateQueries(['contents', variables.org_id, variables.integration_id]);
            queryClient.invalidateQueries(['organisation', variables.org_id]);
        },
        onError: (error: any, variables) => {
            const isUpdate = Boolean(variables.content_id);
            toast.error(isUpdate ? 'Failed to update audio' : 'Failed to upload audio');
            Sentry.captureException(error);
        }
    });
};

interface UpdateAudioTitleParams {
    org_id: string;
    integration_id: string;
    content_id: string;
    title: string;
}

export const useUpdateAudioTitle = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ org_id, integration_id, content_id, title }: UpdateAudioTitleParams) => {
            // Update only title, no URL (no file upload)
            return await updateUploadedAudioService({
                org_id,
                integration_id,
                content_id,
                title,
                url: undefined as any // Backend should handle this gracefully
            });
        },
        onSuccess: (_, variables) => {
            toast.success('Audio title updated successfully');
            
            // Invalidate content queries
            queryClient.invalidateQueries(['contents', variables.org_id, variables.integration_id]);
        },
        onError: (error: any) => {
            toast.error('Failed to update audio title');
            Sentry.captureException(error);
        }
    });
};

